import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import SearchIcon from '@mui/icons-material/Search';
import { colorTokens } from '../theme/tokens';
/**
 * Standardized search field — pixel-perfect match to PondLogs V3-UX.
 *
 * Design spec (from PondLogs-V3-UX/src/app/pages/HelpPage.tsx):
 *   borderRadius : 12px  (rounded-xl)
 *   height       : 28px  (py-1.5 = 6px × 2 + 16px text line-height)
 *   width        : 208px (w-52), 100% on xs
 *   border       : 1px solid #e8edf2 (borderLight) — static, no state changes
 *   font-size    : 12px  (text-xs)
 *   font-weight  : 400
 *   placeholder  : 12px, #646363 (gray600), opacity 1
 *   icon         : Search 11px, absolutely positioned left:10px, #646363
 *   input pl     : 28px (pl-7) to clear the icon
 *
 * Uses absolute-positioned icon (same as V3-UX) to avoid MUI InputAdornment
 * flex-spacing conflicts.
 *
 * Exported from @eruvaka/web-pondlogs-base for use across all domain packages.
 */
export const AppSearchField = (props) => {
    const { value, onChange, placeholder, background = 'filled' } = props;
    const bgColor = background === 'white' ? colorTokens.white : colorTokens.gray50;
    return (_jsxs(Box, { sx: { position: 'relative', flexShrink: 0, width: { xs: '100%', sm: 208 } }, children: [_jsx(SearchIcon, { sx: {
                    position: 'absolute',
                    left: 10,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: 11,
                    color: colorTokens.gray600,
                    pointerEvents: 'none',
                    zIndex: 1,
                } }), _jsx(TextField, { value: value, onChange: (e) => onChange(e.target.value), placeholder: placeholder, inputProps: { 'aria-label': placeholder }, sx: {
                    width: '100%',
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: bgColor,
                        boxShadow: 'none',
                        // Input element — override theme's fixed 34px height
                        '&:not(.MuiInputBase-multiline) .MuiInputBase-input': {
                            height: 'auto',
                            // pl-7 (28px) clears the absolute icon; pr-3 (12px) right padding
                            padding: '6px 12px 6px 28px',
                            fontSize: 12,
                            fontWeight: 400,
                            lineHeight: '16px',
                            boxSizing: 'border-box',
                        },
                        // Placeholder — 12px, gray600, no dimming
                        '& .MuiInputBase-input::placeholder': {
                            color: colorTokens.gray600,
                            opacity: 1,
                            fontSize: 12,
                            fontWeight: 400,
                        },
                        // Fieldset border — set borderRadius here too; theme sets 7px on this element
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: colorTokens.borderLight,
                            borderWidth: '1px',
                            borderRadius: '12px',
                        },
                        // Static border on all states — no colour/weight change
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: colorTokens.borderLight,
                            borderWidth: '1px',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: colorTokens.borderLight,
                            borderWidth: '1px',
                        },
                        '&.Mui-focused': {
                            boxShadow: 'none',
                        },
                    },
                } })] }));
};
